/**
 * apps/battle/client/src/terminal/TerminalApp.tsx
 * Top-level FSM for /terminal route. Mounted by main.tsx when
 * window.location.pathname.startsWith('/terminal'); graph mode at /
 * remains untouched.
 *
 * Screens:
 *   login → title → entryNarrative → question → (feedback inline) → exitNarrative → title
 *                 → portalEntry    → question → portalExit             → title
 *
 * Map / profile are loaded on title entry and refreshed after every submit
 * so the cluster-exit narrative can diff unlock state.
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api.js';
import type { ClusterState, MapResp, PortalState, ProfileResp, SessionResp, SubmitResp } from '../api.js';
import { Login } from '../Login.js';
import '../styles.css';                  // global form/typography from existing client
import './styles/terminal.css';          // terminal palette + chrome
import { TitleScreen } from './screens/TitleScreen.js';
import { EntryNarrativeScreen } from './screens/EntryNarrativeScreen.js';
import { ExitNarrativeScreen } from './screens/ExitNarrativeScreen.js';
import { PortalEntryScreen } from './screens/PortalEntryScreen.js';
import { PortalExitScreen } from './screens/PortalExitScreen.js';
import { QuestionScreen } from './screens/QuestionScreen.js';
import { EmptyClusterScreen } from './screens/EmptyClusterScreen.js';
// Phase 4.2 M6: practice / sandbox mode.
import { PracticeMenu, type PracticeCluster, type PracticePortal } from './screens/PracticeMenu.js';
// Phase 4.2 M7: self-serve auth screens.
import { SignupScreen } from './screens/SignupScreen.js';
import { VerifyEmailSentScreen } from './screens/VerifyEmailSentScreen.js';
// Phase 4.2 M12b-ui1 — client verify-gate (unverified students are 403'd off content routes).
import { VerifyGateScreen } from './screens/VerifyGateScreen.js';
import { ForgotPasswordScreen } from './screens/ForgotPasswordScreen.js';
import { AccountScreen } from './screens/AccountScreen.js';
// Phase 4.2 M10: global leaderboard.
import { LeaderboardScreen } from './screens/LeaderboardScreen.js';
import { diffMap, clusterJustCleared, type UnlockDiff } from './state/unlockDiff.js';

type Screen =
  | { name: 'login' }
  | { name: 'signup' }
  | { name: 'verify-email-sent'; email: string }
  | { name: 'forgot-password' }
  | { name: 'account' }
  | { name: 'leaderboard' }
  | { name: 'bootstrapping' }
  | { name: 'title' }
  | { name: 'cluster-entry'; cluster: ClusterState }
  | { name: 'cluster-question'; cluster: ClusterState }
  | { name: 'cluster-exit'; cluster: ClusterState; xpAwarded: number; diff: UnlockDiff }
  | { name: 'cluster-empty'; cluster: ClusterState }
  | { name: 'portal-entry'; portal: PortalState }
  | { name: 'portal-question'; portal: PortalState }
  | { name: 'portal-exit'; portal: PortalState; xpAwarded: number }
  // Phase 4.2 M6 — practice / sandbox mode (per-cluster, post-clear).
  | { name: 'practice-menu'; fromClusterId: string; initialPortalKey?: string }
  | { name: 'practice-question'; clusterId: string; clusterTitle: string }
  // Phase 4.2 M14 — portal practice (per-portal, post-solve). NO exhaustion.
  | { name: 'portal-practice-question'; portalKey: string; tier: number };

export function TerminalApp() {
  const [screen, setScreen] = useState<Screen>({ name: 'bootstrapping' });
  // Phase 4.1: student also carries role; if SME logs into terminal, redirect to /review.
  const [student, setStudent] = useState<{ id: string; email: string; name: string; role?: string } | null>(null);
  const [map, setMap] = useState<MapResp | null>(null);
  const [profile, setProfile] = useState<ProfileResp | null>(null);
  const [mapSnapshot, setMapSnapshot] = useState<MapResp | null>(null); // pre-cluster snapshot for unlock diff
  // Phase 4.1 fix: bump-on-advance counter used as the React `key` for QuestionScreen,
  // so a same-cluster "fetch next question" actually remounts the screen and re-runs
  // its mount-effect fetchNext(). Without this, setting screen={cluster-question}
  // with the same cluster object is a no-op for the useEffect dependency array.
  const [questionEpoch, setQuestionEpoch] = useState(0);
  // Phase 4.2 M12b-ui1 — true when /api/map returns 403 verify_email_required.
  // Drives the VerifyGateScreen render branch instead of the "loading map…" dead-end.
  const [needsVerify, setNeedsVerify] = useState(false);
  // Phase 4.2 M12h — banner + re-fetch trigger for the return-from-Stripe flow.
  const [billingBanner, setBillingBanner] = useState<string | null>(null);
  const [billingRefresh, setBillingRefresh] = useState(0);

  // Session check on mount
  useEffect(() => {
    api.get<SessionResp>('/api/session')
      .then(async (resp) => {
        if (resp.authenticated && resp.student) {
          setStudent(resp.student);
          await loadMapAndProfile();
          const handled = await maybeHandleBillingReturn();
          if (!handled) setScreen({ name: 'title' });
        } else {
          setScreen({ name: 'login' });
        }
      })
      .catch(() => setScreen({ name: 'login' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 4.2 M12h — handle Stripe's `?billing=` return param. Returns true when
  // it took over routing (so the caller skips its default navigation).
  async function maybeHandleBillingReturn(): Promise<boolean> {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const kind = params.get('billing');
    if (!kind) return false;
    // Strip the param so a refresh doesn't re-fire this handler.
    const url = new URL(window.location.href);
    url.searchParams.delete('billing');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);

    if (kind === 'success') {
      setBillingBanner('Subscription active — thank you! Your plan is now up to date.');
      setScreen({ name: 'account' });
      void refreshUntilTierFlips();
      return true;
    }
    if (kind === 'portal') {
      await loadMapAndProfile();
      setBillingRefresh((n) => n + 1);
      setScreen({ name: 'account' });
      return true;
    }
    if (kind === 'cancel') {
      setBillingBanner('Checkout cancelled — no charge made.');
      setScreen({ name: 'account' });
      return true;
    }
    return false;
  }

  // The webhook may lag the redirect by a second or two, so refresh a few times
  // (immediate → +2s → +5s) until the effective tier flips off FREEMIUM, then stop.
  async function refreshUntilTierFlips(): Promise<void> {
    const delays = [0, 2000, 5000];
    for (const d of delays) {
      if (d > 0) await new Promise((r) => setTimeout(r, d));
      await loadMapAndProfile();
      setBillingRefresh((n) => n + 1);
      try {
        const me = await api.get<{ billing?: { tier?: string } | null }>('/api/account/me');
        if (me.billing?.tier && me.billing.tier !== 'FREEMIUM') break;
      } catch { /* ignore — keep retrying */ }
    }
  }

  async function loadMapAndProfile(): Promise<MapResp | null> {
    let m: MapResp | null = null;
    try {
      m = await api.get<MapResp>('/api/map');
      setMap(m);
      setNeedsVerify(false); // a successful map load clears the gate (re-check path)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        handleLogoutLocal();
      } else if (
        err instanceof ApiError &&
        err.status === 403 &&
        (err.body as { error?: string })?.error === 'verify_email_required'
      ) {
        // M12b verify-gate: authenticated but unverified. Show VerifyGateScreen
        // instead of the infinite "loading map…" dead-end.
        setNeedsVerify(true);
      }
    }
    try {
      const p = await api.get<ProfileResp>('/api/profile');
      setProfile(p);
    } catch { /* non-fatal */ }
    return m;
  }

  function handleLogoutLocal() {
    setStudent(null);
    setMap(null);
    setProfile(null);
    setNeedsVerify(false);
    setScreen({ name: 'login' });
  }

  async function handleLogout() {
    await api.post('/api/logout').catch(() => {});
    handleLogoutLocal();
  }

  async function handleLogin(s: { id: string; email: string; name: string; role?: string }) {
    // Phase 4.1: SMEs / admins who land in terminal mode get redirected to /review.
    if (s.role === 'sme' || s.role === 'admin') {
      window.location.href = '/review';
      return;
    }
    setStudent(s);
    await loadMapAndProfile();
    setScreen({ name: 'title' });
  }

  // Phase 4.2 M7 — post-signup handler: auto-login is already done server-side,
  // we just show the "check your email" screen, then continue to the game.
  async function handleSignupSuccess(s: { id: string; email: string; name: string; role?: string }) {
    if (s.role === 'sme' || s.role === 'admin') {
      window.location.href = '/review';
      return;
    }
    setStudent(s);
    await loadMapAndProfile();
    setScreen({ name: 'verify-email-sent', email: s.email });
  }

  // ----- Screen transitions ----------------------------------------------

  function handleEnterCluster(cluster: ClusterState) {
    setMapSnapshot(map);
    // Empty-cluster guard: use DISTINCT concept count (same source of truth as graph mode).
    if (cluster.conceptTotal === 0) {
      setScreen({ name: 'cluster-empty', cluster });
      return;
    }
    // Phase 4.2 M6 (Option α): cleared clusters route to the PRACTICE MENU
    // instead of the entry narrative. The menu lets students pick any cleared
    // cluster, not just the one they came in on.
    if (cluster.clearedLatched || cluster.stageCleared) {
      setScreen({ name: 'practice-menu', fromClusterId: cluster.clusterId });
      return;
    }
    setScreen({ name: 'cluster-entry', cluster });
  }

  // Phase 4.2 M6 — practice menu chose a cluster (could differ from the one the
  // student originally clicked, since the menu shows all cleared clusters).
  function handlePickPracticeCluster(c: PracticeCluster) {
    setScreen({
      name: 'practice-question',
      clusterId: c.clusterId,
      clusterTitle: c.title || c.clusterId,
    });
  }

  // Phase 4.2 M6 — practice question screen "next question" path. We don't
  // refresh the map (no progress changes in practice), just remount.
  async function handlePracticeAdvance(last?: { xpAwarded: number; newLevel: number; submit?: SubmitResp }) {
    if (screen.name !== 'practice-question') return;
    // Phase 4.2 M6 patch1 — same exhausted guard as cluster/portal. In practice
    // mode the only "exhausted" signal is a 204 from /api/practice/next, which
    // surfaces in QuestionScreen as the exhaustedCluster phase. Bounce back to
    // the PRACTICE MENU rather than title so the student can pick another
    // cluster.
    const isExhaustedAdvance = !last || (last.xpAwarded === 0 && !last.submit);
    if (isExhaustedAdvance) {
      setScreen({ name: 'practice-menu', fromClusterId: screen.clusterId });
      return;
    }
    const xp = last?.xpAwarded ?? 0;
    // XP top-up *can* happen in practice (M4.6) so we refresh profile so the
    // header XP/level updates live. No map reload needed.
    if (xp > 0 || last?.newLevel) {
      try {
        const p = await api.get<ProfileResp>('/api/profile');
        setProfile(p);
      } catch { /* non-fatal */ }
    }
    setQuestionEpoch((e) => e + 1);
    setScreen({
      name: 'practice-question',
      clusterId: screen.clusterId,
      clusterTitle: screen.clusterTitle,
    });
  }

  // Phase 4.2 M14 — practice menu chose a (fully-solved) portal.
  function handlePickPortal(p: PracticePortal) {
    setScreen({ name: 'portal-practice-question', portalKey: p.portalKey, tier: p.tier });
  }

  // Phase 4.2 M14 — portal-practice "next question" path. Mirrors
  // handlePracticeAdvance: a 204 (no template) bounces back to the practice
  // menu; otherwise refresh profile (XP top-up can happen) and remount. Portal
  // practice never exhausts, so the bounce is only for genuinely empty portals.
  async function handlePortalPracticeAdvance(last?: { xpAwarded: number; newLevel: number; submit?: SubmitResp }) {
    if (screen.name !== 'portal-practice-question') return;
    const isExhaustedAdvance = !last || (last.xpAwarded === 0 && !last.submit);
    if (isExhaustedAdvance) {
      setScreen({ name: 'practice-menu', fromClusterId: '' });
      return;
    }
    const xp = last?.xpAwarded ?? 0;
    if (xp > 0 || last?.newLevel) {
      try {
        const p = await api.get<ProfileResp>('/api/profile');
        setProfile(p);
      } catch { /* non-fatal */ }
    }
    setQuestionEpoch((e) => e + 1);
    setScreen({ name: 'portal-practice-question', portalKey: screen.portalKey, tier: screen.tier });
  }

  function handleEnterPortal(portal: PortalState) {
    setMapSnapshot(map);
    // Phase 4.2 M14-patch1 — a FULLY-SOLVED portal lands on the unified practice
    // menu hub first (parity with a cleared cluster), pre-highlighting the clicked
    // portal row. Partially-solved portals keep the normal portal-entry →
    // portal-question play flow (that's where progress is earned).
    if (portal.total > 0 && portal.solved >= portal.total) {
      setScreen({ name: 'practice-menu', fromClusterId: '', initialPortalKey: portal.portalKey });
      return;
    }
    setScreen({ name: 'portal-entry', portal });
  }

  function handleBeginCluster() {
    if (screen.name !== 'cluster-entry') return;
    setScreen({ name: 'cluster-question', cluster: screen.cluster });
  }

  function handleBeginPortal() {
    if (screen.name !== 'portal-entry') return;
    setScreen({ name: 'portal-question', portal: screen.portal });
  }

  async function handleClusterQuestionAdvance(last?: { xpAwarded: number; newLevel: number; submit?: SubmitResp }) {
    if (screen.name !== 'cluster-question') return;
    const cluster = screen.cluster;
    const xp = last?.xpAwarded ?? 0;
    if (last?.newLevel && profile) {
      setProfile({
        ...profile,
        gameState: { ...profile.gameState, level: last.newLevel, totalXp: profile.gameState.totalXp + xp },
      });
    }
    // Phase 4.2 M6 patch1 — BUG FIX (exhausted-cluster infinite loop):
    // QuestionScreen's `exhaustedCluster` phase calls onAdvance() with no args
    // (xp=0, no submit). Pre-patch, that fell into the "remount cluster-question"
    // branch below → re-fetched → exhausted again → loop forever (visible as a
    // flicker between "loading question" and the COMPLETE screen).
    //
    // Detect that path explicitly: no xp + no submit response = nothing to grade,
    // so we must NOT re-enter the question loop. Send the student back to title.
    const isExhaustedAdvance = !last || (last.xpAwarded === 0 && !last.submit);
    if (isExhaustedAdvance) {
      // M10h — the exhausted path previously returned to a STALE title without
      // refreshing the map, so a cluster cleared on its final question still
      // looked not-cleared (and the newly-unlocked cluster stayed hidden) until
      // a hard browser refresh. Refresh first, and if this cluster just cleared,
      // show the SAME cluster-exit narrative the submit branch shows so the
      // unlock reveal fires for ALL players (xp is 0 here — there is no submit).
      const fresh = await loadMapAndProfile();
      if (clusterJustCleared(mapSnapshot, fresh, cluster.clusterId)) {
        const diff = diffMap(mapSnapshot, fresh);
        setScreen({ name: 'cluster-exit', cluster, xpAwarded: last?.xpAwarded ?? 0, diff });
        return;
      }
      // Map is already fresh from the line above — go straight to title (do NOT
      // call the now-reloading handleBackToTitle(), which would double-fetch).
      setScreen({ name: 'title' });
      return;
    }
    const fresh = await loadMapAndProfile();
    // Did this cluster just enter clearedSet? If so → exit narrative.
    if (clusterJustCleared(mapSnapshot, fresh, cluster.clusterId)) {
      const diff = diffMap(mapSnapshot, fresh);
      setScreen({ name: 'cluster-exit', cluster, xpAwarded: xp, diff });
      return;
    }
    // Otherwise stay in question loop — fetch next question by remounting the screen.
    // We re-set the screen with same cluster; QuestionScreen calls fetchNext() on mount.
    setQuestionEpoch((e) => e + 1);
    setScreen({ name: 'cluster-question', cluster: { ...cluster } });
  }

  async function handlePortalQuestionAdvance(last?: { xpAwarded: number; newLevel: number; submit?: SubmitResp }) {
    if (screen.name !== 'portal-question') return;
    const portal = screen.portal;
    const xp = last?.xpAwarded ?? 0;
    if (last?.newLevel && profile) {
      setProfile({
        ...profile,
        gameState: { ...profile.gameState, level: last.newLevel, totalXp: profile.gameState.totalXp + xp },
      });
    }
    // Phase 4.2 M6 patch1 — same exhausted-portal guard as cluster path.
    const isExhaustedAdvance = !last || (last.xpAwarded === 0 && !last.submit);
    if (isExhaustedAdvance) {
      // M10h — refresh map/profile before returning to title so the menu is not
      // stale (no narrative change required for portals).
      await loadMapAndProfile();
      setScreen({ name: 'title' });
      return;
    }
    await loadMapAndProfile();
    // If the just-submitted answer cleared the portal (first deploy) → exit narrative.
    if (last?.submit?.gradeResult.cleared && last.submit.portalDeployed) {
      setScreen({ name: 'portal-exit', portal, xpAwarded: xp });
      return;
    }
    // Else fetch next portal question by remounting.
    setQuestionEpoch((e) => e + 1);
    setScreen({ name: 'portal-question', portal: { ...portal } });
  }

  async function handleBackToTitle() {
    await loadMapAndProfile();
    setScreen({ name: 'title' });
  }

  function handleQuitToGraph() {
    window.location.assign('/');
  }

  // ----- Render -----------------------------------------------------------
  if (screen.name === 'bootstrapping') {
    return (
      <div className="terminal-root">
        <div className="tm-loading">{'>'} booting EconQuest…</div>
      </div>
    );
  }

  // Phase 4.2 M7 — unauthenticated screens (login / signup / forgot-password).
  if (!student && screen.name === 'signup') {
    return (
      <div className="terminal-root">
        <SignupScreen
          onSignupSuccess={(s) => { void handleSignupSuccess(s); }}
          onBackToLogin={() => setScreen({ name: 'login' })}
        />
      </div>
    );
  }

  if (!student && screen.name === 'forgot-password') {
    return (
      <div className="terminal-root">
        <ForgotPasswordScreen onBackToLogin={() => setScreen({ name: 'login' })} />
      </div>
    );
  }

  if (screen.name === 'login' || !student) {
    return (
      <div className="terminal-root">
        <div style={{ maxWidth: 480, margin: '4rem auto', padding: '1rem', color: 'var(--term-fg)' }}>
          <h1 style={{ fontFamily: 'inherit', color: 'var(--term-accent)', textAlign: 'center', marginBottom: '1rem' }}>
            ECONQUEST {'\u2500'} TERMINAL
          </h1>
          <Login
            onLogin={(s) => { void handleLogin(s); }}
            onSignupRequested={() => setScreen({ name: 'signup' })}
            onForgotPasswordRequested={() => setScreen({ name: 'forgot-password' })}
          />
        </div>
      </div>
    );
  }

  // Phase 4.2 M7 — authenticated auxiliary screens.
  if (screen.name === 'verify-email-sent') {
    return (
      <div className="terminal-root">
        <VerifyEmailSentScreen
          email={screen.email}
          onContinue={() => setScreen({ name: 'title' })}
        />
      </div>
    );
  }

  if (screen.name === 'account') {
    return (
      <div className="terminal-root">
        <AccountScreen
          onLoggedOut={() => { handleLogoutLocal(); }}
          onBackToTitle={() => { setBillingBanner(null); setScreen({ name: 'title' }); }}
          notice={billingBanner}
          refreshSignal={billingRefresh}
        />
      </div>
    );
  }

  // Phase 4.2 M10 — global leaderboard (does not require the map).
  if (screen.name === 'leaderboard') {
    return (
      <div className="terminal-root">
        <LeaderboardScreen onBackToTitle={() => setScreen({ name: 'title' })} />
      </div>
    );
  }

  // Phase 4.2 M12b-ui1 — verify GATE. Placed AFTER the auxiliary screens and
  // BEFORE the `if (!map)` dead-end so an unverified student always lands here
  // (with an escape) instead of an infinite "loading map…".
  if (needsVerify) {
    return (
      <div className="terminal-root">
        <VerifyGateScreen
          email={student.email}
          onRecheck={async () => {
            const m = await loadMapAndProfile();
            return m !== null;
          }}
          onLogout={() => { void handleLogout(); }}
        />
      </div>
    );
  }

  if (!map) {
    return (
      <div className="terminal-root">
        <div className="tm-loading">{'>'} loading map…</div>
      </div>
    );
  }

  return (
    <div className="terminal-root">
      {screen.name === 'title' && (
        <TitleScreen
          student={student}
          map={map}
          profile={profile}
          onEnterCluster={handleEnterCluster}
          onEnterPortal={handleEnterPortal}
          onLogout={() => { void handleLogout(); }}
          onQuitToGraph={handleQuitToGraph}
          onOpenAccount={() => setScreen({ name: 'account' })}
          onOpenLeaderboard={() => setScreen({ name: 'leaderboard' })}
          onOpenConceptWeb={() => {
            // M9-patch11 — read-only Concept Web in a new tab; session cookie carries over.
            window.open('/overview?readonly=1', '_blank', 'noopener,noreferrer');
          }}
        />
      )}

      {screen.name === 'cluster-entry' && (
        <EntryNarrativeScreen
          cluster={screen.cluster}
          onBegin={handleBeginCluster}
          onBackToTitle={handleBackToTitle}
        />
      )}

      {screen.name === 'cluster-empty' && (
        <EmptyClusterScreen
          cluster={screen.cluster}
          onBackToTitle={handleBackToTitle}
        />
      )}

      {screen.name === 'cluster-question' && (
        <QuestionScreen
          key={`cluster-${screen.cluster.clusterId}-${questionEpoch}`}
          mode={{ kind: 'cluster', cluster: screen.cluster, battleType: 'REGULAR' }}
          onAdvance={(last) => { void handleClusterQuestionAdvance(last); }}
          onBackToTitle={handleBackToTitle}
        />
      )}

      {screen.name === 'cluster-exit' && (
        <ExitNarrativeScreen
          cluster={screen.cluster}
          xpAwarded={screen.xpAwarded}
          diff={screen.diff}
          onContinue={handleBackToTitle}
        />
      )}

      {screen.name === 'portal-entry' && (
        <PortalEntryScreen
          portal={screen.portal}
          onBegin={handleBeginPortal}
          onBackToTitle={handleBackToTitle}
        />
      )}

      {screen.name === 'portal-question' && (
        <QuestionScreen
          key={`portal-${screen.portal.portalKey}-${questionEpoch}`}
          mode={{ kind: 'portal', portal: screen.portal }}
          onAdvance={(last) => { void handlePortalQuestionAdvance(last); }}
          onBackToTitle={handleBackToTitle}
        />
      )}

      {screen.name === 'portal-exit' && (
        <PortalExitScreen
          portal={screen.portal}
          xpAwarded={screen.xpAwarded}
          onContinue={handleBackToTitle}
        />
      )}

      {/* Phase 4.2 M6 — practice menu */}
      {screen.name === 'practice-menu' && (
        <PracticeMenu
          initialClusterId={screen.fromClusterId}
          initialPortalKey={screen.initialPortalKey}
          onPickCluster={handlePickPracticeCluster}
          onPickPortal={handlePickPortal}
          onBackToTitle={handleBackToTitle}
        />
      )}

      {/* Phase 4.2 M6 — practice question screen */}
      {screen.name === 'practice-question' && (
        <QuestionScreen
          key={`practice-${screen.clusterId}-${questionEpoch}`}
          mode={{
            kind: 'practice',
            clusterId: screen.clusterId,
            clusterTitle: screen.clusterTitle,
          }}
          onAdvance={(last) => { void handlePracticeAdvance(last); }}
          onBackToTitle={handleBackToTitle}
        />
      )}

      {/* Phase 4.2 M14 — portal practice question screen */}
      {screen.name === 'portal-practice-question' && (
        <QuestionScreen
          key={`portal-practice-${screen.portalKey}-${questionEpoch}`}
          mode={{
            kind: 'portal-practice',
            portalKey: screen.portalKey,
            tier: screen.tier,
          }}
          onAdvance={(last) => { void handlePortalPracticeAdvance(last); }}
          onBackToTitle={handleBackToTitle}
        />
      )}
    </div>
  );
}
